import {
  useRef,
  useCallback,
  type ReactElement,
  Children,
  cloneElement,
} from "react";
import useMeasure from "@/view/utils/useMeasure/useMeasure";
import useIsomorphicLayoutEffect from "@/hooks/useIsomorphicLayoutEffect";
import { mergeRefs } from "@/utils/assignRef/assignRef";

const defaultApplyFontSizeFn = (fontSize: number) => `${fontSize}rem`;

const useAdjustFontSizeToFitContent = (
  deps: any[] = [],
  options: {
    min?: number;
    max?: number;
    step?: number;
    applyFontSize?: (fontSize: number) => string;
  } = {},
) => {
  const {
    min = 0.0625,
    max = 1,
    step = 0.0625,
    applyFontSize = defaultApplyFontSizeFn,
  } = options;
  const elRef = useRef<HTMLElement | null>(null);

  const adjustFontSize = useCallback(() => {
    const el = elRef.current;

    if (!el) return;

    const maxHeight = el.offsetHeight;
    const maxWidth = el.offsetWidth;
    let currentSize = max;

    el.style.fontSize = applyFontSize(currentSize);

    let minSize = min;
    let maxSize = max;

    while (maxSize - minSize > step) {
      currentSize = Math.round((minSize + maxSize) / 2 / step) * step;
      el.style.fontSize = applyFontSize(currentSize);

      const fitsHeight = el.scrollHeight <= maxHeight;
      const fitsWidth = el.scrollWidth <= maxWidth;

      if (fitsHeight && fitsWidth) {
        minSize = currentSize;
      } else {
        maxSize = currentSize;
      }
    }

    el.style.fontSize = applyFontSize(minSize);
  }, [min, max, step, applyFontSize]);

  const [measureRef] = useMeasure<HTMLElement>(adjustFontSize);

  useIsomorphicLayoutEffect(() => {
    adjustFontSize();
  }, [adjustFontSize, ...deps]);

  const ref = useCallback((el: HTMLElement | null) => {
    if (!el) return;

    elRef.current = el;
    measureRef(el);
  }, []);

  return ref;
};

export default useAdjustFontSizeToFitContent;

export const AdjustFontSizeToFitContent = ({
  children,
  deps = [],
}: {
  children: ReactElement;
  deps?: any[];
}) => {
  const fontSizeRef = useAdjustFontSizeToFitContent(deps);
  const child = Children.only(children);
  return cloneElement(child, {
    ...child.props,
    ref: mergeRefs(child.props.ref, fontSizeRef),
  });
};
